import React, { useState, useEffect, useRef } from 'react';

const Testimonials = () => {
    const touchTimeoutRef = useRef(null);


    const testimonials = [
        {
            id: 1,
            text: "After a year with Ashima, I feel balanced and understood. Her kind approach helped me progress incredibly.",
            shape: "triangle",
            color: "#FF6B35"
        },
        {
            id: 2,
            text: "Therapy with Ashima is like finally loving my reflection. She's impacted my well-being in unimaginable ways.",
            shape: "circle",
            color: "#FF6B35"
        },
        {
            id: 3,
            text: "Ashima uncovered hidden causes of my depression. Her professional charm made trusting her during difficulties easy.",
            shape: "diamond",
            color: "#FF6B35"
        },
        {
            id: 4,
            text: "Working with Ashima transformed my life. For a therapist who truly cares, I highly recommend her.",
            shape: "triangle",
            color: "#FF6B35"
        },
        {
            id: 5,
            text: "Ashima created a safe space where I share everything without judgment. I feel completely comfortable here.",
            shape: "circle",
            color: "#FF6B35"
        },
        {
            id: 6,
            text: "She found my anxiety's root causes. Her listening skills and understanding supported my tremendous personal growth.",
            shape: "diamond",
            color: "#FF6B35"
        },
        {
            id: 7,
            text: "Her kind approach includes helpful worksheets for each issue. These tools help me understand myself better.",
            shape: "triangle",
            color: "#FF6B35"
        },
        {
            id: 8,
            text: "My anxiety reduced significantly since starting therapy. I understand my feelings clearly and feel much happier.",
            shape: "circle",
            color: "#FF6B35"
        },
        {
            id: 9,
            text: "Ashima is perfect for me. I recommend her to anyone facing difficulties - she'll truly help you.",
            shape: "diamond",
            color: "#FF6B35"
        }
    ];


    const [isPaused, setIsPaused] = useState(false);
    const scrollRef = useRef(null);
    const animationRef = useRef(null);

    useEffect(() => {
        const scrollContainer = scrollRef.current;
        if (!scrollContainer) return;

        let scrollPos = scrollContainer.scrollLeft || 0;
        const scrollSpeed = 0.5;

        const animate = () => {
            if (!isPaused && scrollContainer) {
                scrollPos += scrollSpeed;

                // Get the width of one complete set of testimonials
                const singleSetWidth = scrollContainer.scrollWidth / 2;

                // Reset seamlessly when we've scrolled one complete set
                if (scrollPos >= singleSetWidth) {
                    scrollPos = scrollPos - singleSetWidth;
                }

                scrollContainer.scrollLeft = scrollPos;
            }
            animationRef.current = requestAnimationFrame(animate);
        };

        // Start the animation
        animationRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [isPaused]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (touchTimeoutRef.current) {
                clearTimeout(touchTimeoutRef.current);
            }
        };
    }, []);

    const renderShape = (shape) => {
        // Responsive sizes for shapes - adjusted for better mobile proportions
        const shapeClasses = "w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 xl:w-12 xl:h-12";

        switch (shape) {
            case 'triangle':
                return (
                    <svg className={shapeClasses} viewBox="0 0 32 32" fill="none">
                        <path d="M16 6L26 26H6L16 6Z" fill="#FF6B35" />
                    </svg>
                );
            case 'circle':
                return (
                    <svg className={shapeClasses} viewBox="0 0 32 32" fill="none">
                        <circle cx="16" cy="16" r="13" fill="#FF6B35" />
                    </svg>
                );
            case 'diamond':
                return (
                    <svg className={shapeClasses} viewBox="0 0 32 32" fill="none">
                        <path d="M16 4L28 16L16 28L4 16L16 4Z" fill="#FF6B35" />
                    </svg>
                );
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-[#e3daed] py-16 sm:py-20 lg:py-24 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header Section */}
                <div className="text-center mb-16 sm:mb-20">
                    <div className="font-nunito text-xs sm:text-sm font-medium text-gray-700 mb-6 tracking-[0.2em] uppercase">
                        Client Success Stories
                    </div>
                    <h2 className="font-cormorant font-semibold text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl text-gray-900 mb-6 leading-tight max-w-5xl mx-auto">
                        Hear from Those Who Have Transformed Their Lives
                    </h2>
                    <p className="font-nunito text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                        Real stories from individuals who have found healing, growth, and transformation through compassionate therapeutic care.
                    </p>
                </div>

                {/* Carousel Container */}
                <div
                    className="relative"
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                    onTouchStart={() => {
                        if (touchTimeoutRef.current) {
                            clearTimeout(touchTimeoutRef.current);
                        }
                        setIsPaused(true);
                    }}
                    onTouchEnd={() => {
                        touchTimeoutRef.current = setTimeout(() => {
                            setIsPaused(false);
                        }, 500); // Resume after 500ms
                    }}
                >
                    <div
                        ref={scrollRef}
                        className="flex items-stretch gap-4 sm:gap-6 md:gap-8 lg:gap-10 overflow-x-hidden"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        {/* Double the testimonials for infinite scroll effect */}
                        {[...testimonials, ...testimonials].map((testimonial, index) => (
                            <div
                                key={`${testimonial.id}-${index}`}
                                className="flex-shrink-0 w-[280px] sm:w-[340px] md:w-[380px] lg:w-[420px]"
                            >
                                <div className="backdrop-blur-sm border-2 border-gray-800 p-4 sm:p-6 md:p-8 lg:p-10 flex flex-col justify-between min-h-[220px] sm:min-h-[240px] md:min-h-[260px] lg:min-h-[280px] h-full transition-all duration-500 hover:shadow-xl overflow-hidden">
                                    {/* Quote */}
                                    <p className="font-cormorant text-black text-lg sm:text-xl md:text-2xl lg:text-3xl leading-relaxed flex-grow">
                                        "{testimonial.text}"
                                    </p>

                                    {/* Shape at bottom right */}
                                    <div className="flex justify-end mt-3 sm:mt-4 flex-shrink-0">
                                        {renderShape(testimonial.shape)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Optional: Minimal scroll indicators */}
                <div className="flex justify-center mt-16 gap-3">
                    {[0, 1, 2].map((index) => (
                        <div
                            key={index}
                            className="h-2 w-2 rounded-full bg-gray-400 transition-all duration-500"
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Testimonials; 